import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import RequireAuth from './components/RequireAuth';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import Devis from './components/Devis';
import Factures from './components/Factures';
import Articles from './components/Articles';
import Entreprise from './components/Entreprise';
import AdminTenants from './components/admin/AdminTenants';
import AdminAuditLog from './components/admin/AdminAuditLog';
import AdminEReporting from './components/admin/AdminEReporting';
import AdminContacts from './components/admin/AdminContacts';
import Depenses from './components/Depenses';
import InstallBanner from './components/InstallBanner';
import LandingPage from './components/LandingPage';
import { applyUpdate } from './serviceWorkerRegistration';
import './App.css';

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const tenantLinks = [
    { to: '/', label: 'Tableau de bord' },
    { to: '/devis', label: 'Devis' },
    { to: '/factures', label: 'Factures' },
    { to: '/depenses', label: 'Dépenses' },
    { to: '/articles', label: 'Articles' },
    { to: '/clients', label: 'Clients' },
    { to: '/entreprise', label: 'Paramètres' },
  ];
  const adminLinks = [
    { to: '/admin', label: 'Tenants' },
    { to: '/admin/audit', label: 'Audit' },
    { to: '/admin/e-reporting', label: 'E-reporting' },
    { to: '/admin/contacts', label: 'Messages' },
  ];
  const links = user.role === 'superadmin' ? adminLinks : tenantLinks;

  return (
    <nav className="nav">
      <div className="nav-content">
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/e-facture.png" alt="E-facture" className="nav-logo" />
        </Link>
        <div className="nav-links">
          {links.map(l => (
            <Link key={l.to} to={l.to} className={location.pathname === l.to ? 'active' : ''}>
              {l.label}
            </Link>
          ))}
        </div>
        <div className="nav-user">
          <span className="nav-user-email" title={user.tenant_nom || 'Super administrateur'}>
            {user.tenant_nom || user.email}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={handleLogout}>
            Déconnexion
          </button>
        </div>
      </div>
    </nav>
  );
}

function UpdateBanner({ registration, onDismiss }) {
  const handleRefresh = () => {
    applyUpdate(registration);
    // Recharger après un court délai pour laisser le nouveau SW s'activer
    setTimeout(() => window.location.reload(), 300);
  };
  return (
    <div className="update-banner" role="alert">
      <span>Une mise à jour est disponible.</span>
      <button className="btn btn-sm update-banner-btn" onClick={handleRefresh}>
        Actualiser
      </button>
      <button className="install-banner-close" onClick={onDismiss} aria-label="Fermer">
        ✕
      </button>
    </div>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (!user) return <LandingPage />;
  return <div className="container"><Dashboard /></div>;
}

function App() {
  const [updateRegistration, setUpdateRegistration] = useState(null);

  useEffect(() => {
    const handler = (e) => setUpdateRegistration(e.detail.registration);
    window.addEventListener('swUpdateAvailable', handler);
    return () => window.removeEventListener('swUpdateAvailable', handler);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Navigation />
        <Routes>
          {/* Pages publiques — pas de wrapper container */}
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<HomeRoute />} />
          {/* Pages authentifiées — wrapper container */}
          <Route path="/clients" element={<div className="container"><RequireAuth role="tenant"><Clients /></RequireAuth></div>} />
          <Route path="/devis" element={<div className="container"><RequireAuth role="tenant"><Devis /></RequireAuth></div>} />
          <Route path="/factures" element={<div className="container"><RequireAuth role="tenant"><Factures /></RequireAuth></div>} />
          <Route path="/depenses" element={<div className="container"><RequireAuth role="tenant"><Depenses /></RequireAuth></div>} />
          <Route path="/articles" element={<div className="container"><RequireAuth role="tenant"><Articles /></RequireAuth></div>} />
          <Route path="/entreprise" element={<div className="container"><RequireAuth role="tenant"><Entreprise /></RequireAuth></div>} />
          <Route path="/admin" element={<div className="container"><RequireAuth role="superadmin"><AdminTenants /></RequireAuth></div>} />
          <Route path="/admin/audit" element={<div className="container"><RequireAuth role="superadmin"><AdminAuditLog /></RequireAuth></div>} />
          <Route path="/admin/e-reporting" element={<div className="container"><RequireAuth role="superadmin"><AdminEReporting /></RequireAuth></div>} />
          <Route path="/admin/contacts" element={<div className="container"><RequireAuth role="superadmin"><AdminContacts /></RequireAuth></div>} />
        </Routes>
        <InstallBanner />
        {updateRegistration && (
          <UpdateBanner
            registration={updateRegistration}
            onDismiss={() => setUpdateRegistration(null)}
          />
        )}
      </Router>
    </AuthProvider>
  );
}

export default App;
